import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuthStore } from '../store/authStore';
import { CONFIG } from '../constants/config';
import { portalApi, patrolApi, authApi, adminApi } from '../services/portalApi';
import type { ActiveGuard, HistoryTrack, MapPin, OverduePatrol, PendingAlarm, StillPatrol, WsMessage } from '../types';

interface PortalSocketState {
  activeGuards: Map<string, ActiveGuard>; // keyed by guard_id
  alarmPins: MapPin[];
  incidentPins: MapPin[];
  /** Completed patrols from the last 24h, drawn faded under the live trails. */
  historyTracks: HistoryTrack[];
  pendingAlarm: PendingAlarm | null;
  /** Patrols past their "due back by" whose contacts were told, by patrol_id. */
  overdue: Map<string, OverduePatrol>;
  /** Patrols whose patroller has not moved, contacts told, by patrol_id. */
  still: Map<string, StillPatrol>;
  connected: boolean;
}

export interface UsePortalSocketReturn extends PortalSocketState {
  dismissAlarm: (alarmId: string, notes?: string) => void;
}

// A demo foot patrol ticks once a second for ~25 minutes — about 1500 points —
// so 500 silently dropped the older two thirds of the trail. Real guards ping
// every ~8s, where 2000 points is over four hours.
const MAX_TRACK_POINTS = 2000;

// How far back hydrated alarm/incident pins reach, and how long a pin lives on
// the map. Mobile Chrome suspends background tabs, so the socket is frozen and
// anything raised meanwhile is never delivered — a real incident went missing
// exactly this way. Pins are therefore rebuilt from the DB on every connect
// rather than relying on having been present to receive the event.
const PIN_MAX_AGE_MS = 24 * 60 * 60 * 1000;

// An unacked alarm this recent still deserves the full-screen modal after a
// reconnect. Older than this and it is history, not something to interrupt for.
const ALARM_MODAL_MAX_AGE_MS = 15 * 60 * 1000;

/** raised_at arrives as epoch seconds, epoch ms, or an ISO string depending on
 *  the endpoint. Normalised to epoch ms so pins from different sources sort and
 *  expire consistently. */
function toEpochMs(v: string | number): number {
  if (typeof v === 'number') return v < 1_000_000_000_000 ? v * 1000 : v;
  const n = Number(v);
  if (Number.isFinite(n) && n > 0) return n < 1_000_000_000_000 ? n * 1000 : n;
  const parsed = Date.parse(v);
  return Number.isFinite(parsed) ? parsed : 0;
}

export function usePortalSocket(): UsePortalSocketReturn {
  const accessToken = useAuthStore((s) => s.accessToken);

  const [state, setState] = useState<PortalSocketState>({
    activeGuards: new Map(),
    alarmPins: [],
    incidentPins: [],
    historyTracks: [],
    pendingAlarm: null,
    overdue: new Map(),
    still: new Map(),
    connected: false,
  });

  const wsRef = useRef<WebSocket | null>(null);
  const backoffRef = useRef(CONFIG.WS_BACKOFF_INITIAL_MS);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const heartbeatRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const mountedRef = useRef(true);

  // Rebuild the live map from server state. The WebSocket only ever streams
  // deltas, so a browser refresh (or any reconnect) otherwise starts with an
  // empty map and no breadcrumb until each guard's next location ping —
  // and the trail already walked was lost for good.
  //
  // pt_location_store holds the live sessions; the completed part of each
  // track comes from patrol_points. Both are server-side and survive a
  // refresh, unlike anything held in this hook.
  const hydrate = useCallback(async () => {
    let sessions;
    try {
      sessions = await portalApi.activeSessions();
    } catch {
      return;   // no snapshot available; live updates still populate the map
    }
    const hydrated = await Promise.all(
      sessions.map(async (s) => {
        let track: Array<[number, number]> = [];
        try {
          const points = await patrolApi.track(s.patrol_id);
          track = points
            .slice(-MAX_TRACK_POINTS)
            .map((p) => [p.lat, p.lng] as [number, number]);
        } catch { /* best-effort: a guard with no track still shows a pin */ }
        return { session: s, track };
      }),
    );
    // Recent alarms and incidents, rebuilt from the DB. Uses the existing
    // paginated endpoints — page 1 is newest-first, so the last 24h is always
    // within it at any realistic event rate.
    let alarmPins: MapPin[] = [];
    let incidentPins: MapPin[] = [];
    let recentUnacked: PendingAlarm | null = null;
    let historyTracks: HistoryTrack[] = [];
    const cutoff = Date.now() - PIN_MAX_AGE_MS;
    try {
      const [alarms, incidents, history] = await Promise.all([
        adminApi.listAlarms(1),
        adminApi.listIncidents(1),
        // Best-effort backdrop: a failure here must not cost us the pins, so
        // it resolves to [] rather than rejecting the whole Promise.all.
        adminApi.historyTracks(PIN_MAX_AGE_MS / 3_600_000).catch(() => [] as HistoryTrack[]),
      ]);
      historyTracks = history;
      alarmPins = alarms.data
        .filter((a) => toEpochMs(a.raised_at) > cutoff)
        .map((a) => ({
          id: a.id, patrol_id: a.patrol_id, guard_id: a.guard_id,
          guard_name: a.guard_name, lat: a.lat, lng: a.lng,
          raised_at: toEpochMs(a.raised_at), notes: a.notes,
          ack_notes: a.ack_notes,
        }));
      incidentPins = incidents.data
        .filter((i) => toEpochMs(i.raised_at) > cutoff)
        .map((i) => ({
          id: i.id, patrol_id: i.patrol_id, guard_id: i.guard_id,
          guard_name: i.guard_name, lat: i.lat, lng: i.lng,
          raised_at: toEpochMs(i.raised_at), notes: i.notes,
        }));
      // An alarm raised while the tab was asleep and still unacknowledged is
      // the case that matters most — surface it rather than leaving it as a
      // pin someone has to notice.
      const modalCutoff = Date.now() - ALARM_MODAL_MAX_AGE_MS;
      const pending = alarms.data.find(
        (a) => !a.ack_at && toEpochMs(a.raised_at) > modalCutoff);
      if (pending) {
        recentUnacked = {
          type: 'alarm_raised', alarm_id: pending.id, patrol_id: pending.patrol_id,
          guard_id: pending.guard_id, guard_name: pending.guard_name,
          lat: pending.lat, lng: pending.lng,
          raised_at: toEpochMs(pending.raised_at), received_at: Date.now(),
        };
      }
    } catch { /* best-effort: live events still arrive normally */ }

    if (!mountedRef.current) return;
    setState((prev) => {
      const guards = new Map(prev.activeGuards);
      for (const { session, track } of hydrated) {
        const live = guards.get(session.guard_id);
        // A location update may have landed while these requests were in
        // flight. That position is newer than the snapshot, so keep it and
        // only graft on the history the socket could not have known about.
        guards.set(session.guard_id, {
          patrol_id: session.patrol_id,
          guard_id: session.guard_id,
          guard_name: session.guard_name,
          colour: session.colour,
          lat: live?.lat ?? session.lat,
          lng: live?.lng ?? session.lng,
          heading: live?.heading ?? 0,
          last_seen: live?.last_seen ?? session.last_seen,
          track: [...track, ...(live?.track ?? [])].slice(-MAX_TRACK_POINTS),
        });
      }
      // Overdue comes from the snapshot too: the socket only streams deltas,
      // so a refreshed screen would otherwise lose the warning.
      const overdue = new Map<string, OverduePatrol>();
      for (const { session } of hydrated) {
        if (session.overdue) {
          overdue.set(session.patrol_id, {
            patrol_id: session.patrol_id, guard_id: session.guard_id,
            guard_name: session.guard_name, expected_end_at: session.expected_end_at ?? null,
          });
        }
      }
      const still = new Map<string, StillPatrol>();
      for (const { session } of hydrated) {
        if (session.still) {
          still.set(session.patrol_id, {
            patrol_id: session.patrol_id, guard_id: session.guard_id,
            guard_name: session.guard_name, minutes: session.still_after_min ?? null,
          });
        }
      }
      // Hydrated pins replace whatever was held: the DB is authoritative and
      // includes everything the socket missed.
      return {
        ...prev,
        activeGuards: guards,
        overdue,
        still,
        alarmPins,
        incidentPins,
        historyTracks,
        pendingAlarm: prev.pendingAlarm ?? recentUnacked,
      };
    });
  }, []);

  // The socket authenticates with the access token in its query string, and
  // that token expires. Nothing else refreshes it: the REST client refreshes
  // on a 401, but a WebSocket just gets closed, so the old code reconnected
  // forever with the same dead token — "pt_portal_ws_handler: auth failed
  // token_expired" on a loop — and the live map silently stopped receiving
  // anything: no pins, no guard movement, until a reload.
  //
  // Refreshed BEFORE connecting rather than in response to a failure, because
  // the server closes the socket without telling us why.
  const freshToken = useCallback(async (): Promise<string | null> => {
    const { accessToken: current, refreshToken, payload, clearTokens } =
      useAuthStore.getState();
    if (!current) return null;
    // 60s of slack so a token that expires mid-handshake is renewed first.
    const expiringSoon = !payload || payload.exp * 1000 < Date.now() + 60_000;
    if (!expiringSoon) return current;
    if (!refreshToken) { clearTokens(); return null; }
    try {
      const tokens = await authApi.refresh(refreshToken);
      useAuthStore.getState().setTokens(tokens.access_token, tokens.refresh_token);
      return tokens.access_token;
    } catch {
      // Refresh token dead too — let the normal auth flow move them to login
      // rather than reconnecting in a loop that can never succeed.
      clearTokens();
      return null;
    }
  }, []);

  const connect = useCallback(async () => {
    if (!accessToken || !mountedRef.current) return;

    const token = await freshToken();
    if (!token || !mountedRef.current) return;

    const url = `${CONFIG.WS_URL}?token=${encodeURIComponent(token)}`;
    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => {
      if (!mountedRef.current) { ws.close(); return; }
      backoffRef.current = CONFIG.WS_BACKOFF_INITIAL_MS;
      // Drop the live guard list on every (re)connect, then rebuild it from
      // the server. patrol_ended is only ever broadcast once, so a patrol
      // that ends while the socket is down — every backend restart does this
      // — otherwise leaves a guard pinned to the map forever, and the next
      // roster change adds a second pin beside it. Clearing alone would lose
      // the breadcrumb, hence the hydrate that follows.
      //
      // Alarm/incident pins are deliberately NOT cleared: they are one-shot
      // events with no replay, so dropping them would hide a real unacked
      // alarm until the page is reloaded.
      setState((prev) => ({ ...prev, connected: true, activeGuards: new Map() }));
      void hydrate();
      // Keep-alive heartbeat every 30s
      if (heartbeatRef.current) clearInterval(heartbeatRef.current);
      heartbeatRef.current = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'heartbeat' }));
        }
      }, 30_000);
    };

    ws.onmessage = (event: MessageEvent<string>) => {
      if (!mountedRef.current) return;
      let msg: WsMessage;
      try {
        msg = JSON.parse(event.data) as WsMessage;
      } catch {
        return;
      }
      handleMessage(msg);
    };

    ws.onclose = () => {
      if (!mountedRef.current) return;
      if (heartbeatRef.current) { clearInterval(heartbeatRef.current); heartbeatRef.current = null; }
      setState((prev) => ({ ...prev, connected: false }));
      scheduleReconnect();
    };

    ws.onerror = () => {
      ws.close();
    };
  }, [accessToken, hydrate, freshToken]);

  function scheduleReconnect() {
    if (!mountedRef.current) return;
    const delay = backoffRef.current;
    backoffRef.current = Math.min(delay * 2, CONFIG.WS_BACKOFF_MAX_MS);
    reconnectTimerRef.current = setTimeout(() => { void connect(); }, delay);
  }

  function handleMessage(msg: WsMessage) {
    setState((prev) => {
      const guards = new Map(prev.activeGuards);

      switch (msg.type) {
        case 'patrol_started': {
          guards.set(msg.guard_id, {
            patrol_id: msg.patrol_id,
            guard_id: msg.guard_id,
            guard_name: msg.guard_name,
            colour: msg.colour,
            lat: 0,
            lng: 0,
            heading: 0,
            last_seen: msg.started_at,
            track: [],
          });
          return { ...prev, activeGuards: guards };
        }

        case 'guard_location': {
          const existing = guards.get(msg.guard_id);
          const track = existing?.track ?? [];
          const newTrack: Array<[number, number]> = [
            ...track.slice(-MAX_TRACK_POINTS + 1),
            [msg.lat, msg.lng],
          ];
          guards.set(msg.guard_id, {
            ...(existing ?? {
              patrol_id: msg.patrol_id,
              guard_id: msg.guard_id,
              guard_name: msg.guard_name,
              colour: msg.colour,
            }),
            lat: msg.lat,
            lng: msg.lng,
            heading: msg.heading,
            last_seen: msg.timestamp,
            track: newTrack,
          } as ActiveGuard);
          return { ...prev, activeGuards: guards };
        }

        case 'patrol_ended': {
          guards.delete(msg.guard_id);
          if (prev.overdue.has(msg.patrol_id) || prev.still.has(msg.patrol_id)) {
            const overdue = new Map(prev.overdue);
            overdue.delete(msg.patrol_id);
            const still = new Map(prev.still);
            still.delete(msg.patrol_id);
            return { ...prev, activeGuards: guards, overdue, still };
          }
          // Pins deliberately survive the patrol ending. They used to be
          // cleared here — which is why an incident raised mid-patrol vanished
          // the moment the patroller signed off, before anyone had looked at
          // it. They now expire by age instead (PIN_MAX_AGE_MS), so the map
          // shows what happened recently rather than only what is happening
          // right now.
          return { ...prev, activeGuards: guards };
        }

        case 'patrol_overdue': {
          const overdue = new Map(prev.overdue);
          overdue.set(msg.patrol_id, {
            patrol_id: msg.patrol_id, guard_id: msg.guard_id,
            guard_name: msg.guard_name, expected_end_at: msg.expected_end_at,
          });
          return { ...prev, overdue };
        }

        case 'patrol_still': {
          const still = new Map(prev.still);
          still.set(msg.patrol_id, {
            patrol_id: msg.patrol_id, guard_id: msg.guard_id,
            guard_name: msg.guard_name, minutes: msg.minutes,
          });
          return { ...prev, still };
        }

        case 'patrol_still_cleared': {
          if (!prev.still.has(msg.patrol_id)) return prev;
          const still = new Map(prev.still);
          still.delete(msg.patrol_id);
          return { ...prev, still };
        }

        case 'patrol_overdue_cleared': {
          if (!prev.overdue.has(msg.patrol_id)) return prev;
          const overdue = new Map(prev.overdue);
          overdue.delete(msg.patrol_id);
          return { ...prev, overdue };
        }

        case 'alarm_raised': {
          const alarm: PendingAlarm = { ...msg, received_at: Date.now() };
          const pin: MapPin = {
            id: msg.alarm_id,
            patrol_id: msg.patrol_id,
            guard_id: msg.guard_id,
            guard_name: msg.guard_name,
            lat: msg.lat,
            lng: msg.lng,
            raised_at: msg.raised_at,
          };
          return {
            ...prev,
            pendingAlarm: alarm,
            alarmPins: [...prev.alarmPins, pin],
          };
        }

        case 'incident_raised': {
          const pin: MapPin = {
            id: msg.incident_id,
            patrol_id: msg.patrol_id,
            guard_id: msg.guard_id,
            guard_name: msg.guard_name,
            lat: msg.lat,
            lng: msg.lng,
            raised_at: msg.raised_at,
          };
          return { ...prev, incidentPins: [...prev.incidentPins, pin] };
        }

        case 'alarm_notes_updated': {
          return {
            ...prev,
            alarmPins: prev.alarmPins.map((p) =>
              p.id === msg.alarm_id ? { ...p, notes: msg.notes } : p),
          };
        }

        case 'incident_notes_updated': {
          return {
            ...prev,
            incidentPins: prev.incidentPins.map((p) =>
              p.id === msg.incident_id ? { ...p, notes: msg.notes } : p),
          };
        }

        case 'alarm_acknowledged': {
          // Write ack_notes onto the pin, never notes — the whole point of the
          // split is that acknowledging cannot overwrite what the patroller saw.
          const withAck = {
            ...prev,
            alarmPins: prev.alarmPins.map((p) =>
              p.id === msg.alarm_id ? { ...p, ack_notes: msg.ack_notes ?? null } : p),
          };
          return withAck.pendingAlarm?.alarm_id === msg.alarm_id
            ? { ...withAck, pendingAlarm: null }
            : withAck;
        }

        default:
          return prev;
      }
    });
  }

  const dismissAlarm = useCallback((alarmId: string, notes?: string) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'ack_alarm', alarm_id: alarmId, notes: notes ?? '' }));
    }
    setState((prev) => ({ ...prev, pendingAlarm: null }));
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    void connect();
    return () => {
      mountedRef.current = false;
      if (heartbeatRef.current) clearInterval(heartbeatRef.current);
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      wsRef.current?.close();
    };
  }, [connect]);

  return { ...state, dismissAlarm };
}
